import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';

import useDebouncedValue from '@/hooks/useDebouncedValue';
import useSchedules from '@/hooks/useSchedules';
import type { Schedule } from '@/types/schedule';
import { Alert, Card, Container, Input, Select, Spinner } from '@/ui';
import type { OptionType } from '@/ui/components/Form/Select/types';
import pluralize from '@/utils/pluralize';

import ScheduleCard from './components/ScheduleCard';
import ScheduleDetailPanel from './components/ScheduleDetailPanel';

const uniqueSortedOptions = (values: string[]): OptionType<string>[] =>
  Array.from(new Set(values))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));

const Schedules = () => {
  const { data, isPending, isError } = useSchedules();
  const schedules = useMemo(() => data?.data ?? [], [data]);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 200);
  const [programOption, setProgramOption] = useState<OptionType<string> | null>(null);
  const [typeOption, setTypeOption] = useState<OptionType<string> | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);

  const programOptions = useMemo(
    () =>
      uniqueSortedOptions(schedules.flatMap((schedule) => schedule.programs.map((p) => p.name))),
    [schedules]
  );

  const typeOptions = useMemo(
    () =>
      uniqueSortedOptions(
        schedules.flatMap((schedule) => schedule.expectations.map((e) => e.appointment_type_name))
      ),
    [schedules]
  );

  const results = useMemo(() => {
    const normalizedQuery = debouncedQuery.trim().toLowerCase();

    return schedules.filter((schedule) => {
      if (programOption && !schedule.programs.some((p) => p.name === programOption.value)) {
        return false;
      }
      if (
        typeOption &&
        !schedule.expectations.some((e) => e.appointment_type_name === typeOption.value)
      ) {
        return false;
      }
      if (!normalizedQuery) return true;

      const haystack = [
        schedule.name,
        ...schedule.programs.map((p) => p.name),
        ...schedule.expectations.map((e) => `${e.appointment_type_name} ${e.description}`)
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [schedules, programOption, typeOption, debouncedQuery]);

  const resultLabel =
    results.length === schedules.length
      ? pluralize(schedules.length, 'schedule')
      : `${results.length} of ${pluralize(schedules.length, 'schedule')}`;

  return (
    <Container type="full-width-page">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-2xl font-bold text-uniform-gray-900 dark:text-uniform-gray-100">
            Schedules
          </h1>
          <p className="max-w-[60ch] text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
            A schedule is a named set of appointment expectations. Programs are assigned a schedule;
            expectations are edited on the schedule detail page.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px] flex-1">
            <Input
              label="Search"
              hideLabel
              leadingIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              placeholder="Search schedules, appointment types, programs"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="w-[230px]">
            <Select
              ariaLabel="Filter by program"
              options={programOptions}
              selectedOption={programOption}
              onChange={setProgramOption}
              placeholder="All programs"
            />
          </div>
          <div className="w-[230px]">
            <Select
              ariaLabel="Filter by appointment type"
              options={typeOptions}
              selectedOption={typeOption}
              onChange={setTypeOption}
              placeholder="All appointment types"
            />
          </div>
          <div className="ml-auto pb-2 text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
            {isPending ? 'Loading…' : isError ? null : resultLabel}
          </div>
        </div>

        {isPending ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : isError ? (
          <Alert variant="error">
            Something went wrong loading schedules. Try refreshing the page.
          </Alert>
        ) : results.length === 0 ? (
          <Card className="border-dashed py-14 text-center" padding="none">
            <p className="text-sm text-uniform-gray-600 dark:text-uniform-gray-400">
              No schedules match these filters.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {results.map((schedule) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                onSelect={() => setSelectedSchedule(schedule)}
              />
            ))}
          </div>
        )}
      </div>

      <ScheduleDetailPanel schedule={selectedSchedule} onClose={() => setSelectedSchedule(null)} />
    </Container>
  );
};

export default Schedules;
